import * as Dialog from "@radix-ui/react-dialog";
import { MessageCircle, X } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "src/@/components/ui/button";
import { Input } from "src/@/components/ui/input";
import { trackGoal } from "src/utils/analytics";
import {
  answerDetails,
  buildWhatsappMessage,
  pageUrl,
  whatsappHref,
} from "src/utils/whatsapp";

/*
  АНКЕТА ПЕРЕД WHATSAPP.

  Кнопка «Забронировать» раньше открывала чат сразу. Менеджеру приходило
  название тура — и дальше начинался разговор из пяти вопросов: как вас
  зовут, сколько вас, откуда забирать. Теперь эти вопросы задаёт сайт: два
  поля заполняются за десять секунд, а в чат уходит готовая заявка.

  Анкеты две, набор полей задан здесь:

    basic    — имя и количество человек. Туры и большинство услуг: всё
               остальное про тур уже известно из его карточки.
    transfer — трансфер из аэропорта. Здесь без подробностей нельзя: точки
               подачи и высадки, багаж (машину подбирают по числу и размеру
               чемоданов) и время, к которому нужно подать машину.

  Какую анкету показать услуге, выбирается в админке («Анкета перед WhatsApp»).

  У каждого поля две подписи: `label` — над полем в анкете, где уместен живой
  вопрос («Как вас зовут»), и `msgLabel` — в сообщении, где нужен короткий
  ярлык списка («Имя»).
*/
export const FORMS = {
  basic: [
    { key: "name", label: "form.name", msgLabel: "form.msgName", type: "text", required: true, placeholder: "form.namePlaceholder" },
    { key: "people", label: "form.people", msgLabel: "form.msgPeople", type: "number", min: 1, required: true, defaultValue: "1" },
  ],
  transfer: [
    { key: "name", label: "form.name", msgLabel: "form.msgName", type: "text", required: true, placeholder: "form.namePlaceholder" },
    { key: "from", label: "form.from", msgLabel: "form.msgFrom", type: "text", required: true, placeholder: "form.fromPlaceholder" },
    { key: "to", label: "form.to", msgLabel: "form.msgTo", type: "text", required: true, placeholder: "form.toPlaceholder" },
    { key: "people", label: "form.people", msgLabel: "form.msgPeople", type: "number", min: 1, required: true, defaultValue: "1" },
    { key: "bags", label: "form.bags", msgLabel: "form.msgBags", type: "number", min: 0, defaultValue: "0" },
    {
      key: "bagSize",
      label: "form.bagSize",
      msgLabel: "form.msgBagSize",
      type: "select",
      options: [
        { value: "cabin", label: "form.bagCabin" },
        { value: "medium", label: "form.bagMedium" },
        { value: "large", label: "form.bagLarge" },
      ],
    },
    { key: "date", label: "form.date", msgLabel: "form.msgDate", type: "date", required: true },
    { key: "time", label: "form.time", msgLabel: "form.msgTime", type: "time", required: true },
  ],
};

const initialAnswers = (fields) =>
  Object.fromEntries(fields.map((field) => [field.key, field.defaultValue ?? ""]));

export default function RequestFormModal({
  variant = "basic",
  intro,
  details = [],
  extra = "",
  trigger,
}) {
  const { t } = useTranslation();
  const fields = FORMS[variant] || FORMS.basic;

  const [open, setOpen] = useState(false);
  const [answers, setAnswers] = useState(() => initialAnswers(fields));
  const [error, setError] = useState("");

  const missing = useMemo(
    () => fields.filter((field) => field.required && !String(answers[field.key] || "").trim()),
    [fields, answers]
  );

  const change = (key, value) => {
    setAnswers((prev) => ({ ...prev, [key]: value }));
    setError("");
  };

  const submit = (e) => {
    e.preventDefault();
    if (missing.length > 0) {
      setError(t("form.errorRequired"));
      return;
    }

    // Ответы анкеты идут первыми: менеджер читает сверху вниз, и «кто и
    // сколько человек» важнее, чем даты тура, которые он и так знает.
    const message = buildWhatsappMessage({
      intro,
      details: [...answerDetails(answers, fields, t), ...details],
      extra,
      url: pageUrl(),
    });

    trackGoal("whatsapp", { form: variant });
    window.open(whatsappHref(message), "_blank", "noopener,noreferrer");
    setOpen(false);
  };

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setError("");
      }}
    >
      <Dialog.Trigger asChild>{trigger}</Dialog.Trigger>

      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm data-[state=open]:animate-in data-[state=open]:fade-in-0" />
        {/* max-h + overflow: анкета трансфера на телефоне длиннее экрана */}
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-[92vw] max-w-md max-h-[90svh] overflow-y-auto -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-[#fffef7] p-5 sm:p-6 shadow-xl focus:outline-none">
          <Dialog.Close asChild>
            <button
              aria-label={t("request.close")}
              className="absolute right-2 top-2 flex h-11 w-11 items-center justify-center rounded-md text-neutral-500 hover:text-neutral-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-900"
            >
              <X className="h-5 w-5" aria-hidden="true" />
            </button>
          </Dialog.Close>

          <Dialog.Title className="text-xl font-bold text-neutral-900">
            {t(variant === "transfer" ? "form.transferTitle" : "form.title")}
          </Dialog.Title>
          <Dialog.Description className="mt-1 text-sm text-neutral-500">
            {t("form.subtitle")}
          </Dialog.Description>

          <form onSubmit={submit} className="mt-5 flex flex-col gap-4">
            {fields.map((field) => (
              <div key={field.key} className="flex flex-col gap-1">
                <label htmlFor={`f-${field.key}`} className="text-sm font-medium text-neutral-900">
                  {t(field.label)}
                  {!field.required && (
                    <span className="ml-1 text-neutral-400">{t("form.optional")}</span>
                  )}
                </label>

                {field.type === "select" ? (
                  <select
                    id={`f-${field.key}`}
                    value={answers[field.key]}
                    onChange={(e) => change(field.key, e.target.value)}
                    className="flex h-10 w-full rounded-md border border-neutral-200 bg-white px-3 py-2 text-sm text-neutral-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-900"
                  >
                    <option value="">{t("form.bagSizeAny")}</option>
                    {field.options.map((option) => (
                      <option key={option.value} value={option.value}>
                        {t(option.label)}
                      </option>
                    ))}
                  </select>
                ) : (
                  <Input
                    id={`f-${field.key}`}
                    type={field.type}
                    min={field.min}
                    value={answers[field.key]}
                    onChange={(e) => change(field.key, e.target.value)}
                    placeholder={field.placeholder ? t(field.placeholder) : undefined}
                    aria-required={field.required ? "true" : undefined}
                  />
                )}
              </div>
            ))}

            {error && (
              <p role="alert" className="text-sm font-medium text-red-600">
                {error}
              </p>
            )}

            <Button
              type="submit"
              size="lg"
              className="gap-2 focus-visible:ring-neutral-900 focus-visible:ring-offset-[#fffef7]"
            >
              <MessageCircle aria-hidden="true" className="h-4 w-4" />
              {t("form.submit")}
            </Button>
            <p className="text-xs text-neutral-500">{t("form.hint")}</p>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
